import React, { useState } from 'react';
import { setupPabauAutomation } from '../../../api/onboarding';
import '../OnboardingWizard.css';
import './PabauAutomationSetup.css';
// Import the Pabau logo
import PabauLogo from '../../../assets/images/Pabau_Logo.png';

const PabauSetupStep = ({ onComplete, onBack, leadId }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [connectionStep, setConnectionStep] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);

  const updateProgress = (step, percent) => {
    setConnectionStep(step);
    setProgressPercent(percent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    
    // Start progress animation
    updateProgress('Initializing connection...', 10);

    try {
      // Validate leadId is available
      if (!leadId) {
        setError('Lead ID not found. Please restart the onboarding process.');
        return;
      }

      // Progress updates during connection
      updateProgress('Connecting to Pabau...', 25);
      
      // Add more frequent progress updates to make it feel faster
      setTimeout(() => updateProgress('Loading login page...', 35), 300);
      setTimeout(() => updateProgress('Entering credentials...', 45), 800);
      
      updateProgress('Verifying credentials...', 50);

      const result = await setupPabauAutomation(credentials, leadId);

      if (result.success) {
        updateProgress('Authentication successful!', 80);
        
        // Quick additional progress updates for better UX
        setTimeout(() => updateProgress('Configuring integration...', 90), 400);
        
        // Short delay to show success step
        await new Promise(resolve => setTimeout(resolve, 600));
        
        updateProgress('Connection complete!', 100);
        setSuccess(true);
        
        // Quick success - move user forward
        setTimeout(() => {
          onComplete();
        }, 1000); // Slightly faster transition
      } else {
        // Handle login failures with specific messaging
        const errorMessage = result.error || 'Setup failed. Please check your credentials and try again.';
        
        if (errorMessage.includes('Login failed') || errorMessage.includes('Invalid credentials')) {
          setError(`Login Error: ${errorMessage}\n\nPlease check your Pabau username and password and try again.`);
        } else {
          setError(`Setup Error: ${errorMessage}`);
        }
        
        // Reset progress on error
        setConnectionStep('');
        setProgressPercent(0);
      }
    } catch (error) {
      console.error('Pabau automation error:', error);
      setError(error.message || 'Connection failed. Please try again.');
      setConnectionStep('');
      setProgressPercent(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError(''); // Clear error when user starts typing
  };

  // Render loading spinner with enhanced animation
  const renderLoadingSpinner = () => (
    <div className="loading-container">
      <div className="spinner-wrapper">
        <div className="loading-spinner enhanced"></div>
        <div className="connection-pulse"></div>
      </div>
      <div className="connection-status">
        <div className="status-text">{connectionStep}</div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>
        <div className="progress-percent">{progressPercent}%</div>
      </div>
    </div>
  );

  return (
    <div className="step-content">
      <div className="pabau-setup-container">
        <div className="pabau-card">
          <div className="pabau-header">
            <div className="pabau-icon">
              <img 
                src={PabauLogo} 
                alt="Pabau Logo" 
                onError={(e) => {
                  // Fallback to lightning icon if logo fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <svg 
                width="60" 
                height="60" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="1.5"
                style={{ display: 'none' }}
              >
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <h1>Connect Your Pabau Account</h1>
            <p>This should be an admin account will full sync privileges.</p>
          </div>

          {loading && renderLoadingSpinner()}

          {success && (
            <div className="success-container">
              <div className="success-icon">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22,4 12,14.01 9,11.01"/>
                </svg>
              </div>
              <h2>Connection Successful!</h2>
              <p>Your Pabau account has been connected and automation is being configured in the background.</p>
            </div>
          )}

          {error && (
            <div className="error-container">
              <div className="error-icon">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <div className="error-content">
                <h3>Connection Failed</h3>
                <p>{error}</p>
              </div>
            </div>
          )}

          {!loading && !success && (
            <form onSubmit={handleSubmit} className="pabau-form">
              <div className="form-group">
                <label htmlFor="username">Pabau Username/Email</label>
                <input
                  type="email"
                  id="username"
                  value={credentials.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="your-email@clinic.com"
                  required
                  disabled={loading}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Pabau Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={credentials.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Your Pabau password"
                    required
                    disabled={loading}
                    className="form-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={onBack}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !credentials.username || !credentials.password}
                >
                  {loading ? 'Connecting...' : 'Connect to Pabau'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="pabau-features">
          <h3>What happens next?</h3>
          <ul>
            <li>
              <span className="feature-icon">🔐</span>
              <span>We securely verify your Pabau login credentials</span>
            </li>
            <li>
              <span className="feature-icon">🔑</span>
              <span>Automatically extract your API key (no manual copying needed)</span>
            </li>
            <li>
              <span className="feature-icon">🔗</span>
              <span>Configure webhooks for real-time data synchronization</span>
            </li>
            <li>
              <span className="feature-icon">⚡</span>
              <span>Start syncing your patient data immediately</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PabauSetupStep;